import React, { createContext, useContext, ReactNode, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLiveShowContext } from './LiveShowContext';
import { useNewShowDetection } from '../hooks/data/useNewShowDetection';
import { useNewShowTimer } from '../hooks/timers/useNewShowTimer';
import { useShowDetection } from '../hooks/timers/useShowDetection';
import { Show } from '../types/Show';
import { debugError, debugVerbose } from '../utils/debugLog';

interface GlobalLiveShowChangeContextType {
  /**
   * Subscribe to live show change events
   * @param callback Function to call when live show changes
   * @returns Unsubscribe function
   */
  onLiveShowChange: (callback: (newShow: Show | null, previousShow: Show | null) => void) => () => void;
  
  /**
   * Subscribe to new past show available events
   * @param callback Function to call when new past show becomes available
   * @returns Unsubscribe function
   */
  onNewPastShowAvailable: (callback: (newShows: Show[]) => void) => () => void;
  
  /**
   * Current live show (for convenience)
   */
  currentLiveShow: Show | null;
  
  /**
   * Manually trigger live show change detection (for testing)
   */
  triggerChangeDetection: () => void;
  
  /**
   * Manually trigger new show detection (for testing)
   */
  triggerNewShowDetection: () => void;
}

const GlobalLiveShowChangeContext = createContext<GlobalLiveShowChangeContextType | undefined>(undefined);

interface GlobalLiveShowChangeProviderProps {
  children: ReactNode;
}

export const GlobalLiveShowChangeProvider: React.FC<GlobalLiveShowChangeProviderProps> = ({ children }) => {
  // Use existing LiveShowContext for live show data
  const { currentLiveShow, loading } = useLiveShowContext();
  
  // Query client for cache invalidation
  const queryClient = useQueryClient();
  
  // Track previous live show for change detection
  const previousLiveShowRef = useRef<Show | null>(null);
  
  // Store subscribers for live show changes
  const liveShowSubscribersRef = useRef<Set<(newShow: Show | null, previousShow: Show | null) => void>>(new Set());
  
  // Store subscribers for new past show events
  const newPastShowSubscribersRef = useRef<Set<(newShows: Show[]) => void>>(new Set());
  
  // Global new show detection hooks
  const { refetch: refetchRecentShows } = useNewShowDetection({ enabled: false });
  const showDetection = useShowDetection({ todayOnly: true, enableLogging: true });
  
  // Keep track of previous shows for comparison
  const previousShowsRef = useRef<Show[]>([]);
  
  // Global new show detection function
  const detectNewShows = useCallback(async (): Promise<boolean> => {
    try {
      debugVerbose('🌐 GlobalLiveShowChange: Checking for new shows globally...');
      
      // Refetch recent shows data (only last 2 shows)
      const result = await refetchRecentShows();
      
      if (result && result.data) {
        // Use the recent shows for detection (only 2 shows instead of all)
        const detection = showDetection.detectNewShows(result.data);
        
        if (detection.hasNewShows) {
          debugVerbose(`🎉 GlobalLiveShowChange: Found ${detection.totalCount} new show(s):`, 
            detection.newShows.map((s: Show) => s.show));
          
          // STEP 4.3.3: Global cache invalidation
          debugVerbose('🗑️ GlobalLiveShowChange: Invalidating all past show caches...');
          await queryClient.invalidateQueries({ queryKey: ['shows'] });
          debugVerbose('✅ GlobalLiveShowChange: Cache invalidation complete');
          
          // Broadcast new past show available event
          debugVerbose('📡 GlobalLiveShowChange: Broadcasting new past show event to', newPastShowSubscribersRef.current.size, 'subscribers');
          newPastShowSubscribersRef.current.forEach(callback => {
            try {
              callback(detection.newShows);
            } catch (error) {
              debugError('🌐 GlobalLiveShowChange: Error in new past show subscriber callback:', error);
            }
          });
          
          // Update baseline for future comparisons
          previousShowsRef.current = result.data;
          return true; // Stop polling - new shows found
        } else {
          debugVerbose('🔍 GlobalLiveShowChange: No new shows found, continuing to poll...');
          return false; // Continue polling
        }
      }
      
      return false; // Continue polling if no data
    } catch (error) {
      debugError('🌐 GlobalLiveShowChange: Error during new show detection:', error);
      return false; // Continue polling on error
    }
  }, [refetchRecentShows, showDetection, queryClient]);
  
  // Global new show timer
  const newShowTimer = useNewShowTimer({
    detectNewShows,
    enableLogging: true,
    initialDelay: 5 * 60 * 1000, // 5 minutes
    activeInterval: 60 * 1000,   // 1 minute when active
    inactiveInterval: 5 * 60 * 1000, // 5 minutes when inactive
    maxRetries: 30,
  });
  
  // Broadcast live show change to all subscribers
  const broadcastLiveShowChange = useCallback((newShow: Show | null, previousShow: Show | null) => {
    debugVerbose('🌐 GlobalLiveShowChange: Broadcasting live show change to', liveShowSubscribersRef.current.size, 'subscribers');
    debugVerbose('🌐 Previous show:', previousShow?.show || 'None');
    debugVerbose('🌐 New show:', newShow?.show || 'None');
    
    liveShowSubscribersRef.current.forEach(callback => {
      try {
        callback(newShow, previousShow);
      } catch (error) {
        debugError('🌐 GlobalLiveShowChange: Error in live show subscriber callback:', error);
      }
    });
    
    // When live show changes, trigger new show detection timer
    debugVerbose('🌐 GlobalLiveShowChange: Live show changed - starting new show detection timer');
    newShowTimer.triggerNewShowCheck();
  }, [newShowTimer]);
  
  // Subscribe to live show changes
  const onLiveShowChange = useCallback((callback: (newShow: Show | null, previousShow: Show | null) => void) => {
    liveShowSubscribersRef.current.add(callback);
    debugVerbose('🌐 GlobalLiveShowChange: Live show subscriber added, total:', liveShowSubscribersRef.current.size);
    
    // Return unsubscribe function
    return () => {
      liveShowSubscribersRef.current.delete(callback);
      debugVerbose('🌐 GlobalLiveShowChange: Live show subscriber removed, total:', liveShowSubscribersRef.current.size);
    };
  }, []);
  
  // Subscribe to new past show events
  const onNewPastShowAvailable = useCallback((callback: (newShows: Show[]) => void) => {
    newPastShowSubscribersRef.current.add(callback);
    debugVerbose('🌐 GlobalLiveShowChange: New past show subscriber added, total:', newPastShowSubscribersRef.current.size);
    
    // Return unsubscribe function
    return () => {
      newPastShowSubscribersRef.current.delete(callback);
      debugVerbose('🌐 GlobalLiveShowChange: New past show subscriber removed, total:', newPastShowSubscribersRef.current.size);
    };
  }, []);
  
  // Manual trigger for testing live show changes
  const triggerChangeDetection = useCallback(() => {
    debugVerbose('🌐 GlobalLiveShowChange: Manual live show change detection triggered');
    const previous = previousLiveShowRef.current;
    const current = currentLiveShow;
    
    // Force broadcast even if shows are the same (for testing)
    broadcastLiveShowChange(current, previous);
  }, [currentLiveShow, broadcastLiveShowChange]);
  
  // Manual trigger for testing new show detection
  const triggerNewShowDetection = useCallback(() => {
    debugVerbose('🌐 GlobalLiveShowChange: Manual new show detection triggered');
    newShowTimer.triggerNewShowCheck();
  }, [newShowTimer]);
  
  // Detect live show changes
  useEffect(() => {
    // Skip if still loading
    if (loading) return;
    
    const previous = previousLiveShowRef.current;
    const current = currentLiveShow;
    
    // Check if live show actually changed
    const hasChanged = (
      // Different show names
      previous?.show !== current?.show ||
      // Different start times (same show name but different time slot)
      previous?.date_start_time !== current?.date_start_time
    );
    
    if (hasChanged) {
      debugVerbose('🌐 GlobalLiveShowChange: Live show change detected!');
      broadcastLiveShowChange(current, previous);
    }
    
    // Update previous show reference
    previousLiveShowRef.current = current;
  }, [currentLiveShow, loading, broadcastLiveShowChange]);
  
  const contextValue: GlobalLiveShowChangeContextType = {
    onLiveShowChange,
    onNewPastShowAvailable,
    currentLiveShow,
    triggerChangeDetection,
    triggerNewShowDetection,
  };

  return (
    <GlobalLiveShowChangeContext.Provider value={contextValue}>
      {children}
    </GlobalLiveShowChangeContext.Provider>
  );
};

export const useGlobalLiveShowChange = (): GlobalLiveShowChangeContextType => {
  const context = useContext(GlobalLiveShowChangeContext);
  if (context === undefined) {
    throw new Error('useGlobalLiveShowChange must be used within a GlobalLiveShowChangeProvider');
  }
  return context;
};
